/**
 * `/agents/templates` — Agent cookbook / gallery (SMB pre-built templates).
 *
 * Displays a curated set of pre-configured agent templates. Each template
 * ships with a system prompt, tool packages, and an optional trigger
 * assignment. One click deploys the template: create agent → enable tools →
 * assign trigger (if any) → navigate to the new agent's detail page.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCreateAgent,
  useUpdateAgentToolPackages,
  useUpsertTrigger,
} from "@/hooks/use-agents";
import type { NativeToolPackageName } from "@simpliwork/api-schema";

// ── Template definitions ──────────────────────────────────────────────────────

type TriggerType = "lead_qualify" | "lead_book" | "content_draft";

interface AgentTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  useCases: string[];
  tools: NativeToolPackageName[];
  trigger?: TriggerType;
  systemPrompt: string;
}

const TEMPLATES: AgentTemplate[] = [
  {
    id: "lead-qualifier",
    name: "Lead Qualifier",
    category: "Lead Management",
    description: "Automatically qualifies inbound leads with smart questions and updates your CRM.",
    useCases: ["Budget & timeline discovery", "Lead scoring", "Hot-lead escalation"],
    tools: ["crm", "sms"],
    trigger: "lead_qualify",
    systemPrompt: `You are a friendly, professional sales assistant helping qualify inbound leads for a small business.

Your job is to have a short, conversational SMS exchange that uncovers:
1. What the lead is looking for (their goal or pain point)
2. Their budget range (ask gently — "What budget are you working with?")
3. Their timeline (when do they need this?)
4. Decision-making authority (are they the decision maker?)

Guidelines:
- Keep messages short — 1–3 sentences max per SMS.
- Be warm and human, not robotic.
- If a lead is highly qualified (clear budget, imminent timeline, decision maker), update their CRM status to "qualified" and let them know you'll be in touch shortly.
- If they're not a fit right now, politely let them know and mark them as "lost" with a note.
- Never send more than 4 messages in a single conversation thread without a response.
- Do not make promises about pricing or timelines you cannot keep.`,
  },
  {
    id: "booking-bot",
    name: "Booking Bot",
    category: "Lead Management",
    description: "Sends your booking link, handles objections, and confirms appointments via SMS.",
    useCases: ["Scheduling appointments", "Handling 'not now' objections", "Appointment reminders"],
    tools: ["crm", "sms", "calendar"],
    trigger: "lead_book",
    systemPrompt: `You are a helpful scheduling assistant for a small business. Your goal is to get qualified leads onto the calendar.

Your approach:
1. Send the booking link from your calendar tool with a short, friendly message.
2. If the lead has questions, answer them briefly and re-invite them to book.
3. If they push back ("I'm not ready yet"), acknowledge it, ask when would be better, and offer to follow up then.
4. Once they confirm a booking, thank them and set the CRM status to "booked".

Guidelines:
- Be concise — leads are busy.
- Always include the booking link when appropriate.
- Do not pressure or send more than 3 messages without a reply.
- If the lead goes cold after 3 attempts, mark them "contacted" and stop reaching out.`,
  },
  {
    id: "followup-agent",
    name: "Follow-up Agent",
    category: "Lead Management",
    description: "Re-engages cold leads with personalised SMS nudges at the right moment.",
    useCases: ["Re-engagement campaigns", "Quote follow-ups", "Checking in after demos"],
    tools: ["crm", "sms"],
    systemPrompt: `You are a friendly follow-up specialist for a small business. Your job is to re-engage leads who have gone cold or not responded to previous outreach.

Your approach:
1. Look up the lead's history in the CRM to understand where the conversation left off.
2. Send a warm, personalised message that references something specific (e.g., "You were looking at our landscaping services last month — are you still interested?").
3. Keep it short, low-pressure, and easy to respond to.
4. Offer a clear next step: a quick call, a booking link, or just a simple yes/no question.

Guidelines:
- Never send the same message twice — vary your approach each time.
- Maximum 2 follow-up attempts per re-engagement sequence.
- If there's still no response after 2 messages, mark the lead as "lost" with a note explaining the follow-up attempts.
- Be human. Avoid templates that sound like mass marketing.`,
  },
  {
    id: "content-creator",
    name: "Content Creator",
    category: "Content",
    description: "Drafts engaging social media posts on demand, ready for your review and scheduling.",
    useCases: ["Facebook & Instagram posts", "LinkedIn thought leadership", "Twitter updates"],
    tools: ["content"],
    trigger: "content_draft",
    systemPrompt: `You are a social media content writer for a small business. Your job is to draft high-quality, engaging posts that sound authentic — not like generic AI content.

When asked to create content:
1. Write in a conversational, human tone that matches a small business owner talking to their community.
2. Lead with a hook — a question, a surprising stat, a bold statement, or a relatable situation.
3. Keep posts appropriately short for the platform (Facebook/Instagram: 50–150 words; Twitter: under 280 characters; LinkedIn: 100–300 words).
4. Include a clear call-to-action where appropriate (book a free consultation, visit our website, reply with a question, etc.).
5. Use line breaks and white space for readability. Avoid walls of text.

What to avoid:
- Generic openers like "In today's world..." or "As a business owner..."
- Excessive hashtags (1–3 max, placed at the end)
- Overly promotional language
- Emojis used just to fill space

Always return the draft ready to post — no meta-commentary or explanations needed.`,
  },
  {
    id: "review-responder",
    name: "Review Responder",
    category: "Customer Success",
    description: "Crafts professional, personalised responses to customer reviews and feedback.",
    useCases: ["Google review responses", "Complaint handling", "Positive review acknowledgement"],
    tools: ["crm", "sms"],
    systemPrompt: `You are a customer success representative for a small business. Your job is to respond to customer reviews and feedback professionally and authentically.

For positive reviews:
- Thank the customer by name if available.
- Acknowledge the specific thing they mentioned (great service, fast delivery, etc.).
- Invite them to come back or refer a friend.
- Keep it short — 2–3 sentences.

For negative or mixed reviews:
- Acknowledge their experience without being defensive.
- Apologise sincerely for any shortcomings.
- Offer to make it right — provide a contact method (phone, email) or offer to reach out directly.
- Do not argue or make excuses.
- End on a positive, forward-looking note.

General guidelines:
- Sound like a real person, not a corporate PR department.
- Keep responses concise — under 100 words for most reviews.
- Never promise refunds or discounts in a public response; handle those privately.
- Update the CRM with a note about any customer who left a negative review so the team can follow up.`,
  },
  {
    id: "invoice-followup",
    name: "Invoice Follow-up",
    category: "Finance",
    description: "Sends polite payment reminders via SMS and tracks outstanding invoices in your CRM.",
    useCases: ["Overdue invoice reminders", "Payment confirmation", "Escalation to phone call"],
    tools: ["crm", "sms"],
    systemPrompt: `You are a polite but firm accounts receivable assistant for a small business. Your job is to follow up on outstanding invoices via SMS.

Your sequence:
1. First reminder (3 days after due date): Friendly — "Hi [Name], just a quick reminder that invoice #[X] for $[amount] was due on [date]. Let us know if you have any questions!"
2. Second reminder (7 days overdue): Firmer — "Hi [Name], we wanted to follow up on invoice #[X] which is now a week overdue. Please arrange payment at your earliest convenience or contact us to discuss options."
3. Final notice (14 days overdue): Serious — "Hi [Name], this is our final notice regarding invoice #[X] ($[amount], due [date]). Please make payment or call us at [phone] today to avoid further action."

Guidelines:
- Always be polite — no hostile language.
- Reference the invoice number and amount specifically to avoid confusion.
- If the customer says they've already paid, check the CRM notes and apologise if they have — update the status immediately.
- If they request a payment plan, update the CRM with the agreed terms and note it for the business owner to review.
- Never threaten legal action in a message — only flag to the business owner for escalation.`,
  },
];

// ── Category colours ──────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  "Lead Management": "bg-blue-50 text-blue-700 border-blue-200",
  "Content": "bg-purple-50 text-purple-700 border-purple-200",
  "Customer Success": "bg-green-50 text-green-700 border-green-200",
  "Finance": "bg-amber-50 text-amber-700 border-amber-200",
};

// ── Tool labels ───────────────────────────────────────────────────────────────

const TOOL_LABELS: Record<NativeToolPackageName, string> = {
  crm: "CRM",
  sms: "SMS",
  calendar: "Calendar",
  content: "Content",
};

const TRIGGER_LABELS: Record<TriggerType, string> = {
  lead_qualify: "Auto: new lead",
  lead_book:    "Auto: qualify → book",
  content_draft: "Auto: draft request",
};

// ── Template card ─────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: AgentTemplate;
  onDeploy: (template: AgentTemplate) => void;
  isDeploying: boolean;
}

function TemplateCard({ template, onDeploy, isDeploying }: TemplateCardProps) {
  const categoryColor = CATEGORY_COLORS[template.category] ?? "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <Card className="flex flex-col border-gray-200 h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${categoryColor}`}>
            {template.category}
          </span>
          {template.trigger && (
            <span className="inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
              {TRIGGER_LABELS[template.trigger]}
            </span>
          )}
        </div>
        <CardTitle className="text-base font-bold text-gray-900">{template.name}</CardTitle>
        <p className="text-sm text-muted-foreground leading-relaxed">{template.description}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 flex-1">
        <ul className="flex flex-col gap-1">
          {template.useCases.map((uc) => (
            <li key={uc} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="text-gray-400">›</span> {uc}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {template.tools.map((tool) => (
            <Badge key={tool} variant="secondary" className="text-[11px]">
              {TOOL_LABELS[tool]}
            </Badge>
          ))}
        </div>
        <Button
          size="sm"
          className="w-full"
          onClick={() => onDeploy(template)}
          disabled={isDeploying}
        >
          {isDeploying ? "Deploying…" : "Deploy this template"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AgentTemplatesPage() {
  const navigate = useNavigate();
  const createAgent = useCreateAgent();
  const updateToolPackages = useUpdateAgentToolPackages();
  const upsertTrigger = useUpsertTrigger();

  const [deploying, setDeploying] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);

  async function handleDeploy(template: AgentTemplate) {
    setDeploying(template.id);
    setDeployError(null);
    try {
      // 1. Create the agent with the template's system prompt and sane defaults.
      const { agent_id } = await createAgent.mutateAsync({
        name: template.name,
        description: template.description,
        config: {
          backend: "anthropic_messages",
          system_prompt: template.systemPrompt,
          // M1.2: Sonnet 4.6 is the 1× reference model. Legacy stored
          // strings auto-upgrade at dispatch.
          model: "claude-sonnet-4-6",
          // M1.7 (BYOK teardown) — LLM keys are operator secrets; no
          // tenant secrets needed for the LLM call.
          secrets_refs: [],
          mcp_bindings: [],
          allowed_egress: ["*"],
        },
        limits: {
          timeout_seconds: 120,
          max_tokens: 4096,
          max_cost_micros: 1_000_000,
        },
      });

      // 2. Enable the template's tool packages.
      if (template.tools.length > 0) {
        await updateToolPackages.mutateAsync({
          agentId: agent_id,
          body: { enabled: template.tools },
        });
      }

      // 3. Assign trigger if specified (overwrites any existing assignment).
      if (template.trigger) {
        await upsertTrigger.mutateAsync({
          triggerType: template.trigger,
          body: { agent_id, enabled: true },
        });
      }

      navigate(`/agents/${agent_id}`);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : "Failed to deploy template.");
    } finally {
      setDeploying(null);
    }
  }

  // Group templates by category for display.
  const categories = Array.from(new Set(TEMPLATES.map((t) => t.category)));

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">
            Agents
          </p>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Template Gallery</h1>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-xl mt-1">
            Pre-built agents for common SMB workflows. Deploy any template in one click — it creates
            the agent, enables the right tools, and wires up any automation triggers automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/agents")}
          className="text-xs font-bold uppercase tracking-[0.2em] text-primary hover:underline"
        >
          ← My agents
        </button>
      </header>

      {deployError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {deployError}
        </div>
      )}

      {categories.map((category) => (
        <section key={category}>
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-3">
            {category}
          </h2>
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {TEMPLATES.filter((t) => t.category === category).map((template) => (
              <li key={template.id}>
                <TemplateCard
                  template={template}
                  onDeploy={handleDeploy}
                  isDeploying={deploying === template.id}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default AgentTemplatesPage;
